import React from 'react';
import { css } from '@emotion/react';
import { isMobile } from '$/utils/isMobile';
import { AnimatePresence, motion } from 'framer-motion';

import {
  SSOLoginButtonInRoom,
  useSSOAuth,
} from '$/sections/Home/Profile/TeamIntegrations/Info/SSO/SSOLogin';

import { withWebinarPlayerContext } from '../context';
import { HasJoined, PlayerMode, PlayerPlacement } from '../types';
import { PlayButton } from '../Controls/PlayButton';
import { useEmbeddedPlayerContext } from '../../EmbeddedPlayer/useEmbeddedPlayer';

import { RoomType } from '$/GraphQL/schema/types';
import { WidgetEventType } from '$/widget/src/interface';

import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useVideoStageArea } from '$/hooks/useVideoStageArea';
import { useImageDimensions } from '$/hooks/useImageDimensions';
import { useDetectNotSupportedBrowser } from '$/sections/PublishedWebinar/ViewWebinar/BlockUnsupportedBrowsers';
import { NotSupported } from '$/components/pages/404/NotSupported';
import { useAttendeeContext } from '$/sections/PublishedWebinar/ViewWebinar/AttendeeContext';
import { useServerContext } from '$/hooks/useServerDateNow';

export const PlayerOverlay = withWebinarPlayerContext(
  [
    'attendeeId',
    'mode',
    'set',
    'player',
    'loaded',
    'animations',
    'currentRoom',
    'dimensions',
    'placement',
    'clickedJoin',
    'setShowOverlay',
    'setIsPlaying',
    'hasJoined',
    'cardInteractions',
    'setActionsPaneState',
    'showingInVideoFormBeforeWatching',
    'connectionId',
    'shortIdRef',
  ],
  'EmbeddedPlayerJoinRoom'
)(({
  context: {
    attendeeId,
    mode,
    set,
    player,
    loaded,
    animations,
    currentRoom,
    dimensions,
    placement,
    clickedJoin,
    setShowOverlay,
    setIsPlaying,
    hasJoined,
    cardInteractions,
    setActionsPaneState,
    showingInVideoFormBeforeWatching,
    connectionId,
    shortIdRef,
  },
}) => {
  const [overlayExited, setOverlayExited] = React.useState(false);
  const [clickedPlayButton, setClickedPlayButton] = React.useState(false);
  const [showOverlayBackground, setShowOverlayBackground] = React.useState(true);
  const [creatingAttendee, setCreatingAttendee] = React.useState(false);

  const { serverDateNow } = useServerContext();
  const { authorized } = useSSOAuth(set, attendeeId);
  const { onReadyToJoin, onJoined, toggleExpand, playerInfo, isExpanded } =
    useEmbeddedPlayerContext();
  const { join } = useAttendeeContext();
  const thumbnail = playerInfo?.thumbnail;
  const { loaded: thumbnailLoaded } = useImageDimensions(thumbnail);
  const imageLoaded = !thumbnail || thumbnailLoaded;
  const isUnSupported = useDetectNotSupportedBrowser(placement === PlayerPlacement.EmbeddedPlayer);
  const [style, animateStyle] = useVideoStageArea({
    animations,
    currentRoom,
    dimensions,
  });

  const onOverlayExit = React.useCallback(() => {
    setOverlayExited(true);
  }, [setOverlayExited]);

  const onPlayButtonClick = React.useCallback(async (): Promise<void> => {
    if (!loaded) return;
    // create real attendee before start the video
    setCreatingAttendee(true);
    await join({
      joinTime: serverDateNow().toISOString(),
      connectionId: connectionId ? connectionId : shortIdRef.current,
    });
    setCreatingAttendee(false);
    setClickedPlayButton(true);
    onJoined?.();
    toggleExpand?.(true);
    if (mode === PlayerMode.replay) {
      !showingInVideoFormBeforeWatching && setIsPlaying(true);
    } else {
      clickedJoin();
    }
  }, [
    setClickedPlayButton,
    onJoined,
    toggleExpand,
    mode,
    setIsPlaying,
    clickedJoin,
    loaded,
    join,
    showingInVideoFormBeforeWatching,
    connectionId,
    serverDateNow,
    shortIdRef,
  ]);

  const readyToJoin = loaded && player && imageLoaded;
  React.useEffect(() => {
    if (!readyToJoin) return;
    // wait for the video
    onReadyToJoin?.(dimensions.video.aspectRatio);
  }, [
    dimensions.video.aspectRatio,
    imageLoaded,
    loaded,
    onReadyToJoin,
    player,
    isExpanded,
    readyToJoin,
  ]);

  React.useEffect(() => {
    if (!showOverlayBackground || !isExpanded || !readyToJoin || showingInVideoFormBeforeWatching)
      return;
    // only hide the background when not collapsed
    setShowOverlayBackground(false);
  }, [showOverlayBackground, isExpanded, readyToJoin, showingInVideoFormBeforeWatching]);

  const shouldShowOverlay =
    currentRoom.type === RoomType.Presentation &&
    placement === PlayerPlacement.EmbeddedPlayer &&
    (hasJoined !== HasJoined.Joined || mode === PlayerMode.replay);
  const shouldShowOverlayChanged = useHasChangedShallow([shouldShowOverlay]);
  React.useEffect(() => {
    if (!shouldShowOverlayChanged) return;
    setShowOverlay(Boolean(shouldShowOverlay));
  }, [setShowOverlay, shouldShowOverlay, shouldShowOverlayChanged]);

  const shouldCheckAndSetShowOverlayState = useHasChangedShallow([
    overlayExited,
    hasJoined,
    setShowOverlay,
    setActionsPaneState,
    currentRoom,
    showingInVideoFormBeforeWatching,
  ]);
  React.useEffect(() => {
    if (!shouldCheckAndSetShowOverlayState) return;
    if (!overlayExited || hasJoined !== HasJoined.Joined || showingInVideoFormBeforeWatching)
      return;

    setShowOverlay(false);
    if (!cardInteractions.length) return;
    if (currentRoom.type === RoomType.PostExit) return;

    setActionsPaneState('shown');
  }, [
    overlayExited,
    hasJoined,
    setShowOverlay,
    setActionsPaneState,
    currentRoom,
    showingInVideoFormBeforeWatching,
    shouldCheckAndSetShowOverlayState,
    cardInteractions.length,
  ]);

  const currentRoomChanged = useHasChangedShallow([currentRoom]);
  React.useEffect(() => {
    if (placement !== PlayerPlacement.EmbeddedPlayer) return;
    if (currentRoom.type === RoomType.Presentation) return;
    window.xprops?.emit({
      event: WidgetEventType.PlayerHadComeToExitRoom,
    });
  }, [currentRoom, currentRoomChanged, placement]);

  React.useEffect(() => {
    if (isUnSupported && placement === PlayerPlacement.EmbeddedPlayer) {
      window.xprops?.emit({
        event: WidgetEventType.UnSupportedBrowser,
      });
    }
  }, [isUnSupported, placement]);

  const buttonSize = (isMobile ? 50 : 70) / style.scale;
  const loadingCircleWidth = 5 / style.scale;

  if (!isExpanded) {
    // sometimes we still see a small part of the video at the right side when collapsed
    // add 2px to fix it.
    style.width += 2;
    if (animateStyle) animateStyle.width += 2;
  }

  if (!shouldShowOverlay || overlayExited) return null;

  if (isUnSupported && placement === PlayerPlacement.EmbeddedPlayer) {
    return (
      <motion.div
        animate={{
          width: isExpanded ? '100%' : `${playerInfo.width}px`,
          height: isExpanded ? '100%' : `${playerInfo.height}px`,
        }}
        className='absolute top-0 left-0 z-player-embedded-overlay bg-white'
      >
        <NotSupported
          url={playerInfo.href}
          isEmbeddedPlayer
          small={!isExpanded}
          inapp
          className={!isExpanded ? 'h-auto py-2' : undefined}
        />
        ;
      </motion.div>
    );
  }

  return (
    <motion.div className='absolute inset-0 z-player-embedded-overlay'>
      <motion.div
        className='absolute top-0 left-0 right-0 bottom-0 overflow-hidden'
        css={css`
          transform-origin: top left;
        `}
        initial={style}
        animate={animateStyle}
        transition={{
          duration: 0.1,
          type: 'tween',
        }}
      >
        <AnimatePresence onExitComplete={onOverlayExit}>
          {!clickedPlayButton && (
            <motion.div
              className='relative'
              exit={{
                opacity: 0,
                transitionEnd: {
                  display: 'none',
                },
                transition: {
                  duration: 1,
                  type: 'tween',
                },
              }}
              css={css`
                width: 100%;
                height: 100%;
                background-size: cover;
                background-position: center;
              `}
            >
              <div
                className='absolute inset-0'
                css={css`
                  background-size: cover;
                  background-position: center;
                  ${thumbnail ? `background-image: url(${thumbnail});` : `background: #e7e9ec;`}

                  transition: opacity 0.5s;
                  opacity: ${showOverlayBackground ? `1` : '0'};
                `}
              />
              <div className='absolute inset-0 flex flex-col items-center justify-center'>
                {playerInfo.playButtonBackgroundColor && (
                  <div
                    className='absolute inset-0'
                    css={css`
                      background: ${playerInfo.playButtonBackgroundColor};
                      ${playerInfo.playButtonBackgroundBlur
                        ? `backdrop-filter: blur(${
                            playerInfo.playButtonBackgroundBlur / style.scale
                          }px);`
                        : ``}
                      border-radius: ${(isExpanded ? dimensions.videoStage.radius : 20) /
                      style.scale}px;
                    `}
                  />
                )}
                <div
                  className='flex flex-col cursor-pointer justify-center items-center z-1'
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlayButtonClick();
                  }}
                >
                  <div
                    css={css`
                      width: ${buttonSize}px;
                      height: ${buttonSize}px;
                      margin: ${5 / style.scale}px;
                      @keyframes ewebinar_spin {
                        from {
                          transform: rotate(0deg);
                        }
                        to {
                          transform: rotate(360deg);
                        }
                      }
                      #playButton {
                        width: ${buttonSize}px;
                        height: ${buttonSize}px;
                        svg {
                          backdrop-filter: blur(${loadingCircleWidth}px);
                          circle {
                            fill-opacity: 0;
                          }
                        }
                        &::before {
                          content: '';
                          position: absolute;
                          top: -${loadingCircleWidth}px;
                          left: -${loadingCircleWidth}px;
                          width: ${buttonSize + loadingCircleWidth * 2}px;
                          height: ${buttonSize + loadingCircleWidth * 2}px;
                          border: ${loadingCircleWidth}px solid #d0d0d0;
                          border-right-color: transparent;
                          border-bottom-color: transparent;
                          border-radius: 50%;
                          box-sizing: border-box;
                          transform-origin: center center;
                          animation: ewebinar_spin 1s linear infinite;
                          visibility: hidden;
                        }
                        &.loading {
                          &::before {
                            visibility: visible;
                          }
                        }
                      }
                    `}
                  >
                    {!authorized ? (
                      <SSOLoginButtonInRoom set={set} attendeeId={attendeeId} />
                    ) : (
                      <PlayButton
                        invert
                        showPlayIcon
                        className={!loaded || creatingAttendee ? 'loading' : ''}
                        onClick={onPlayButtonClick}
                      />
                    )}
                  </div>
                  {playerInfo.playButtonCTAText && (
                    <div
                      css={css`
                        z-index: 1;
                        font-size: ${(playerInfo.playButtonCTAFontSize ?? 14) / style.scale}px;
                        line-height: 1.25;
                        font-family: Arial, Helvetica, sans-serif;
                        ${playerInfo.playButtonCTATextColor
                          ? `color: ${playerInfo.playButtonCTATextColor};`
                          : ``}
                      `}
                    >
                      {playerInfo.playButtonCTAText}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
});
